import {
  hexToRgb,
  hsvToRgb,
  maxOklchChromaInSrgb,
  normalizeHex,
  oklchToRgb,
  rgbToHex,
  rgbToOklch,
  sanitizeHexInput,
  type Oklch,
} from '../math/color.js';
import { clamp01 } from '../math/scalar.js';
import type { ValueSubtype, ValueUnit } from '../graph/nodeDefs.js';
import { createDragger, type DragFieldElement } from './controls/dragField.js';

declare global {
  interface Window {
    EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> };
  }
}

export interface ColorPickerOptions {
  /** Element the popover is anchored to (typically the swatch button). */
  anchor: HTMLElement;
  /** Initial color as `#RRGGBB`. */
  hex: string;
  /** Optional opacity stored as a 0..1 fraction. */
  opacity?: number;
  /** Called (rAF-coalesced) with `#RRGGBB` while the color changes. */
  onInput: (hex: string) => void;
  /** Called immediately with opacity as a 0..1 fraction while it changes. */
  onOpacityInput?: (opacity: number) => void;
  /** Called once when an interaction starts (for history batching). */
  onBegin?: () => void;
  /** Called when an interaction settles (drag end, field commit, eyedrop). */
  onCommit?: () => void;
  /** Called after the popover is dismissed. */
  onClose?: () => void;
}

export interface ColorPickerHandle {
  /** Push a new color into the open popover (e.g. external hex edits). */
  setHex: (hex: string) => void;
  /** Push a new opacity into the open popover (e.g. external alpha edits). */
  setOpacity: (opacity: number) => void;
  close: () => void;
  readonly element: HTMLDivElement;
}

const OKLCH_PICKER_MAX_CHROMA = 0.34;
const OKLCH_PICKER_HUE_RING_INNER_RADIUS = 0.86;
const OKLCH_PICKER_HUE_HIT_INNER_RADIUS = 0.80;
const OKLCH_PICKER_FIELD_RATIO = 0.59;

type ColorPickerDragMode = 'field' | 'hue';

let activePicker: { close: () => void } | null = null;

function wrapHue(value: number): number {
  return ((value % 360) + 360) % 360;
}

export function openColorPicker(options: ColorPickerOptions): ColorPickerHandle {
  activePicker?.close();

  let color = rgbToOklch(hexToRgb(options.hex));
  const hasOpacity = typeof options.opacity === 'number' && typeof options.onOpacityInput === 'function';
  let opacity = hasOpacity ? clamp01(options.opacity ?? 1) : 1;
  let closed = false;

  const popover = document.createElement('div');
  popover.className = 'color-picker-popover';
  popover.setAttribute('role', 'dialog');
  popover.setAttribute('aria-label', 'OKLCH color picker');

  const header = document.createElement('div');
  header.className = 'color-picker-header';

  const summary = document.createElement('div');
  summary.className = 'color-picker-summary';
  const title = document.createElement('div');
  title.className = 'color-picker-title';
  title.textContent = 'OKLCH';
  const cssValue = document.createElement('div');
  cssValue.className = 'color-picker-css-value';
  summary.appendChild(title);
  summary.appendChild(cssValue);
  header.appendChild(summary);
  popover.appendChild(header);

  const plane = document.createElement('div');
  plane.className = 'color-picker-plane';

  const wheel = document.createElement('div');
  wheel.className = 'color-picker-wheel';
  wheel.setAttribute('aria-label', 'Hue, lightness, and chroma');
  const wheelCanvas = document.createElement('canvas');
  wheelCanvas.className = 'color-picker-wheel-canvas';
  wheel.appendChild(wheelCanvas);
  const hueRing = document.createElement('canvas');
  hueRing.className = 'color-picker-wheel-hue-ring';
  wheel.appendChild(hueRing);
  const wheelThumb = document.createElement('div');
  wheelThumb.className = 'color-picker-wheel-thumb color-picker-glass-thumb';
  wheel.appendChild(wheelThumb);
  const hueThumb = document.createElement('div');
  hueThumb.className = 'color-picker-wheel-hue-thumb color-picker-glass-thumb';
  wheel.appendChild(hueThumb);
  plane.appendChild(wheel);

  const lightness = document.createElement('div');
  lightness.className = 'color-picker-lightness';
  lightness.setAttribute('aria-label', 'Lightness');
  const lightnessCanvas = document.createElement('canvas');
  lightnessCanvas.className = 'color-picker-lightness-canvas';
  lightness.appendChild(lightnessCanvas);
  const lightnessThumb = document.createElement('div');
  lightnessThumb.className = 'color-picker-lightness-thumb';
  lightness.appendChild(lightnessThumb);
  plane.appendChild(lightness);
  popover.appendChild(plane);

  const hexRow = document.createElement('div');
  hexRow.className = 'color-picker-row';

  const hexInput = document.createElement('input');
  hexInput.className = 'color-picker-hex';
  hexInput.type = 'text';
  hexInput.spellcheck = false;
  hexInput.setAttribute('aria-label', 'Hex color');
  hexRow.appendChild(hexInput);

  const eyeButton = document.createElement('button');
  eyeButton.type = 'button';
  eyeButton.className = 'color-picker-eyedropper';
  eyeButton.title = 'Pick from screen';
  eyeButton.setAttribute('aria-label', 'Pick color from screen');
  eyeButton.appendChild(createEyeDropperIcon());
  if (typeof window === 'undefined' || typeof window.EyeDropper !== 'function') {
    eyeButton.hidden = true;
  }
  hexRow.appendChild(eyeButton);
  popover.appendChild(hexRow);

  const oklchRow = document.createElement('div');
  oklchRow.className = 'color-picker-oklch';
  const oklchControls = {
    // Lightness stored as 0..1; 'percentage' (fraction unit) shows it as 0..100%.
    l: createOklchControl('Lightness', color.l, 0, 1, 0.001, undefined, 1, value => {
      setColor({ ...color, l: value });
    }, options, 'percentage', undefined, true),
    c: createOklchControl('Chroma', color.c, 0, OKLCH_PICKER_MAX_CHROMA, 0.001, undefined, 3, value => {
      setColor({ ...color, c: value });
    }, options, 'color', undefined, true),
    // Hue stored in degrees; 'angle' with the 'degrees' unit shows it as-is (no
    // radian conversion), keeping it a real angle field.
    h: createOklchControl('Hue', color.h, 0, 360, 1, undefined, 0, value => {
      setColor({ ...color, h: value });
    }, options, 'angle', 'degrees', true),
  };
  const opacityControl = hasOpacity
    ? createOklchControl('Opacity', opacity, 0, 1, 0.01, undefined, 0, value => {
      setOpacity(value);
    }, options, 'percentage', undefined, true)
    : null;
  oklchRow.appendChild(oklchControls.l.wrap);
  oklchRow.appendChild(oklchControls.c.wrap);
  oklchRow.appendChild(oklchControls.h.wrap);
  if (opacityControl) oklchRow.appendChild(opacityControl.wrap);
  popover.appendChild(oklchRow);

  document.body.appendChild(popover);

  let rafId = 0;
  let pendingHex: string | null = null;
  const flush = (): void => {
    rafId = 0;
    if (pendingHex === null) return;
    const hex = pendingHex;
    pendingHex = null;
    options.onInput(hex);
  };
  const emit = (hex: string): void => {
    pendingHex = hex;
    if (rafId === 0) rafId = requestAnimationFrame(flush);
  };
  const flushPendingInput = (): void => {
    if (rafId === 0) return;
    cancelAnimationFrame(rafId);
    flush();
  };

  const currentRgb = (clipped?: { value: boolean }) => oklchToRgb(outputOklch(color), clipped);
  const currentHex = (): string => rgbToHex(...currentRgb());
  let renderedWheelKey = '';
  let renderedLightnessKey = '';
  let renderedHueRingKey = '';

  const maxChroma = (l: number, h: number, cap = OKLCH_PICKER_MAX_CHROMA): number =>
    maxOklchChromaInSrgb(l, h, cap);

  const renderWheel = (): void => {
    const rect = wheel.getBoundingClientRect();
    const cssSize = Math.max(1, Math.round(Math.min(rect.width, rect.height)));
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const size = Math.max(1, Math.round(cssSize * scale));
    const wheelKey = `${size}:${color.h.toFixed(4)}`;
    if (wheelCanvas.width === size && wheelCanvas.height === size && renderedWheelKey === wheelKey) {
      return;
    }
    if (wheelCanvas.width !== size || wheelCanvas.height !== size) {
      wheelCanvas.width = size;
      wheelCanvas.height = size;
    }
    renderedWheelKey = wheelKey;

    const ctx = wheelCanvas.getContext('2d');
    if (!ctx) return;
    const innerSide = size * OKLCH_PICKER_FIELD_RATIO;
    const innerOffset = (size - innerSide) * 0.5;
    const image = ctx.createImageData(size, size);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const offset = (y * size + x) * 4;
        const localX = x - innerOffset;
        const localY = y - innerOffset;
        const outsideField = localX < 0 || localX > innerSide || localY < 0 || localY > innerSide;
        if (outsideField) {
          image.data[offset + 3] = 0;
          continue;
        }
        const l = clamp01(1 - localY / innerSide);
        const requestedC = clamp01(localX / innerSide) * OKLCH_PICKER_MAX_CHROMA;
        const gamutC = maxChroma(l, color.h);
        const rgb = oklchToRgb({ l, c: Math.min(requestedC, gamutC), h: color.h });
        image.data[offset] = Math.round(rgb[0] * 255);
        image.data[offset + 1] = Math.round(rgb[1] * 255);
        image.data[offset + 2] = Math.round(rgb[2] * 255);
        image.data[offset + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
    ctx.save();
    const strokeOffset = Math.max(1, scale) * 0.5;
    const strokeSize = innerSide - Math.max(1, scale);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.16)';
    ctx.lineWidth = Math.max(1, scale);
    ctx.strokeRect(
      innerOffset + strokeOffset,
      innerOffset + strokeOffset,
      strokeSize,
      strokeSize,
    );
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.strokeRect(
      innerOffset + strokeOffset,
      innerOffset + strokeOffset,
      strokeSize,
      strokeSize,
    );
    ctx.restore();
  };

  const renderLightness = (): void => {
    const rect = lightnessCanvas.getBoundingClientRect();
    const cssWidth = Math.max(1, Math.round(rect.width));
    const cssHeight = Math.max(1, Math.round(rect.height));
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(cssWidth * scale));
    const height = Math.max(1, Math.round(cssHeight * scale));
    const key = `${width}x${height}:${color.c.toFixed(4)}:${color.h.toFixed(2)}`;
    if (lightnessCanvas.width !== width || lightnessCanvas.height !== height) {
      lightnessCanvas.width = width;
      lightnessCanvas.height = height;
    } else if (renderedLightnessKey === key) {
      return;
    }
    renderedLightnessKey = key;

    const ctx = lightnessCanvas.getContext('2d');
    if (!ctx) return;
    const image = ctx.createImageData(width, height);
    for (let y = 0; y < height; y++) {
      const l = 1 - (height <= 1 ? 0 : y / (height - 1));
      const rgb = oklchToRgb({ l, c: Math.min(color.c, maxChroma(l, color.h)), h: color.h });
      for (let x = 0; x < width; x++) {
        const offset = (y * width + x) * 4;
        image.data[offset] = Math.round(rgb[0] * 255);
        image.data[offset + 1] = Math.round(rgb[1] * 255);
        image.data[offset + 2] = Math.round(rgb[2] * 255);
        image.data[offset + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
  };

  const renderHueRing = (): void => {
    const rect = wheel.getBoundingClientRect();
    const cssSize = Math.max(1, Math.round(Math.min(rect.width, rect.height)));
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const size = Math.max(1, Math.round(cssSize * scale));
    if (hueRing.width !== size || hueRing.height !== size) {
      hueRing.width = size;
      hueRing.height = size;
    } else if (renderedHueRingKey === `${size}`) {
      return;
    }
    renderedHueRingKey = `${size}`;

    const ctx = hueRing.getContext('2d');
    if (!ctx) return;
    const image = ctx.createImageData(size, size);
    const center = (size - 1) * 0.5;
    const radius = size * 0.5;
    const aa = 1.25 / radius;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x - center) / radius;
        const dy = (y - center) / radius;
        const r = Math.hypot(dx, dy);
        const offset = (y * size + x) * 4;
        const outerAlpha = clamp01((1 - r) / aa + 0.5);
        const innerAlpha = clamp01((r - OKLCH_PICKER_HUE_RING_INNER_RADIUS) / aa + 0.5);
        const alpha = Math.min(outerAlpha, innerAlpha);
        if (alpha <= 0) {
          image.data[offset + 3] = 0;
          continue;
        }
        const hue = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
        const rgb = hsvToRgb({ h: hue, s: 1, v: 1 });
        image.data[offset] = Math.round(rgb[0] * 255);
        image.data[offset + 1] = Math.round(rgb[1] * 255);
        image.data[offset + 2] = Math.round(rgb[2] * 255);
        image.data[offset + 3] = Math.round(alpha * 255);
      }
    }
    ctx.putImageData(image, 0, 0);
  };

  const syncControls = (): void => {
    const rgb = currentRgb();
    const hex = rgbToHex(...rgb);
    const hueColor = rgbToHex(...hsvToRgb({ h: color.h, s: 1, v: 1 }));
    const hueRad = (color.h * Math.PI) / 180;
    popover.style.setProperty('--picker-color', hex);
    popover.style.setProperty('--picker-hue-color', hueColor);
    popover.style.setProperty('--picker-lightness', `${color.l * 100}%`);
    popover.style.setProperty('--picker-chroma', color.c.toFixed(3));
    popover.style.setProperty('--picker-hue', `${color.h.toFixed(1)}deg`);
    popover.style.setProperty('--picker-opacity', `${opacity * 100}%`);
    const wheelRect = wheel.getBoundingClientRect();
    const wheelSize = Math.min(wheelRect.width, wheelRect.height);
    if (wheelSize > 0) {
      const innerSide = wheelSize * OKLCH_PICKER_FIELD_RATIO;
      const innerOffset = (wheelSize - innerSide) / 2;
      const wheelX = innerOffset + clamp01(color.c / OKLCH_PICKER_MAX_CHROMA) * innerSide;
      const wheelY = innerOffset + (1 - color.l) * innerSide;
      wheelThumb.style.left = `${wheelX}px`;
      wheelThumb.style.top = `${wheelY}px`;
    } else {
      wheelThumb.style.left = '50%';
      wheelThumb.style.top = '50%';
    }
    if (wheelSize > 0) {
      const hueThumbRadius =
        wheelSize * 0.5 * ((1 + OKLCH_PICKER_HUE_RING_INNER_RADIUS) / 2);
      const hueThumbX = wheelSize * 0.5 + Math.cos(hueRad) * hueThumbRadius;
      const hueThumbY = wheelSize * 0.5 + Math.sin(hueRad) * hueThumbRadius;
      hueThumb.style.left = `${hueThumbX}px`;
      hueThumb.style.top = `${hueThumbY}px`;
      hueThumb.style.display = '';
    } else {
      hueThumb.style.display = 'none';
    }
    lightnessThumb.style.top = `${(1 - color.l) * 100}%`;
    renderWheel();
    renderLightness();
    renderHueRing();

    if (document.activeElement !== hexInput) hexInput.value = hex;
    oklchControls.l.setValue(color.l);
    oklchControls.c.setValue(color.c);
    oklchControls.h.setValue(color.h);
    opacityControl?.setValue(opacity);
    const alphaSuffix = hasOpacity ? ` / ${(opacity * 100).toFixed(0)}%` : '';
    const output = outputOklch(color);
    cssValue.textContent = `oklch(${(output.l * 100).toFixed(1)}% ${output.c.toFixed(3)} ${output.h.toFixed(0)}${alphaSuffix})`;
  };

  const setColor = (next: Oklch, emitChange = true): void => {
    color = normalizeOklch(next);
    syncControls();
    if (emitChange) emit(currentHex());
  };

  function setOpacity(next: number, emitChange = true): void {
    if (!hasOpacity) return;
    opacity = clamp01(next);
    syncControls();
    if (emitChange) options.onOpacityInput?.(opacity);
  }

  const dragTrack = (track: HTMLElement, onMove: (event: PointerEvent) => void): void => {
    const update = (event: PointerEvent): void => onMove(event);
    track.addEventListener('pointerdown', (event: PointerEvent) => {
      event.preventDefault();
      options.onBegin?.();
      track.setPointerCapture(event.pointerId);
      update(event);
      const onPointerMove = (e: PointerEvent): void => update(e);
      const onPointerUp = (e: PointerEvent): void => {
        track.releasePointerCapture(e.pointerId);
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('pointerup', onPointerUp);
        flushPendingInput();
        options.onCommit?.();
      };
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', onPointerUp);
    });
  };

  const getWheelDragMode = (event: PointerEvent): ColorPickerDragMode | null => {
    const rect = wheel.getBoundingClientRect();
    const cx = rect.left + rect.width * 0.5;
    const cy = rect.top + rect.height * 0.5;
    const dx = event.clientX - cx;
    const dy = event.clientY - cy;
    const wheelSize = Math.min(rect.width, rect.height);
    if (!wheelSize) return null;
    const wheelRadiusNorm = Math.hypot(dx, dy) / (wheelSize * 0.5);
    const innerSide = wheelSize * OKLCH_PICKER_FIELD_RATIO;
    const innerOffset = (wheelSize - innerSide) / 2;
    const localX = (event.clientX - rect.left) - innerOffset;
    const localY = (event.clientY - rect.top) - innerOffset;
    const inField = localX >= 0 && localX <= innerSide && localY >= 0 && localY <= innerSide;
    if (inField) return 'field';
    if (wheelRadiusNorm >= OKLCH_PICKER_HUE_HIT_INNER_RADIUS && wheelRadiusNorm <= 1.04) return 'hue';
    return null;
  };

  const setFromWheelPointer = (event: PointerEvent, mode: ColorPickerDragMode): void => {
    const rect = wheel.getBoundingClientRect();
    const cx = rect.left + rect.width * 0.5;
    const cy = rect.top + rect.height * 0.5;
    const dx = event.clientX - cx;
    const dy = event.clientY - cy;
    const wheelSize = Math.min(rect.width, rect.height);
    if (!wheelSize) return;
    if (mode === 'field') {
      const innerSide = wheelSize * OKLCH_PICKER_FIELD_RATIO;
      const innerOffset = (wheelSize - innerSide) / 2;
      const localX = (event.clientX - rect.left) - innerOffset;
      const localY = (event.clientY - rect.top) - innerOffset;
      const l = clamp01(1 - localY / innerSide);
      const c = clamp01(localX / innerSide) * OKLCH_PICKER_MAX_CHROMA;
      setColor({ ...color, l, c });
      return;
    }
    const hue = Math.atan2(dy, dx) * (180 / Math.PI);
    setColor({ ...color, h: wrapHue(hue) });
  };

  wheel.addEventListener('pointerdown', event => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    const dragMode = getWheelDragMode(event);
    if (!dragMode) return;
    event.preventDefault();
    options.onBegin?.();
    wheel.setPointerCapture(event.pointerId);
    const onPointerMove = (e: PointerEvent): void => {
      setFromWheelPointer(e, dragMode);
    };
    const onPointerUp = (e: PointerEvent): void => {
      wheel.releasePointerCapture(e.pointerId);
      wheel.removeEventListener('pointermove', onPointerMove);
      wheel.removeEventListener('pointerup', onPointerUp);
      wheel.removeEventListener('pointercancel', onPointerUp);
      flushPendingInput();
      options.onCommit?.();
    };
    setFromWheelPointer(event, dragMode);
    wheel.addEventListener('pointermove', onPointerMove);
    wheel.addEventListener('pointerup', onPointerUp);
    wheel.addEventListener('pointercancel', onPointerUp);
  });

  dragTrack(lightness, event => {
    const rect = lightnessCanvas.getBoundingClientRect();
    setColor({ ...color, l: 1 - clamp01((event.clientY - rect.top) / rect.height) });
  });

  hexInput.addEventListener('input', () => {
    const sanitized = sanitizeHexInput(hexInput.value);
    if (hexInput.value !== sanitized) hexInput.value = sanitized;
    const normalized = normalizeHex(sanitized);
    if (normalized) setColorFromHex(normalized, false);
  });
  const commitHex = (): void => {
    const normalized = normalizeHex(hexInput.value);
    if (normalized) {
      options.onBegin?.();
      setColorFromHex(normalized);
      flushPendingInput();
    }
    else syncControls();
    options.onCommit?.();
  };
  hexInput.addEventListener('change', commitHex);
  hexInput.addEventListener('blur', commitHex);

  function setColorFromHex(hex: string, emitChange = true): void {
    setColor(rgbToOklch(hexToRgb(hex)), emitChange);
  }

  eyeButton.addEventListener('click', () => {
    if (typeof window.EyeDropper !== 'function') return;
    const dropper = new window.EyeDropper();
    dropper
      .open()
      .then(result => {
        if (closed) return;
        const normalized = normalizeHex(result.sRGBHex);
        if (normalized) {
          options.onBegin?.();
          setColorFromHex(normalized);
          flushPendingInput();
          options.onCommit?.();
        }
      })
      .catch(() => {
        /* user cancelled */
      });
  });

  const position = (): void => {
    const rect = options.anchor.getBoundingClientRect();
    const width = popover.offsetWidth;
    const height = popover.offsetHeight;
    const margin = 6;
    let left = rect.left;
    let top = rect.bottom + margin;
    if (left + width > window.innerWidth - margin) left = window.innerWidth - width - margin;
    if (left < margin) left = margin;
    if (top + height > window.innerHeight - margin) top = rect.top - height - margin;
    if (top < margin) top = margin;
    popover.style.left = `${Math.round(left)}px`;
    popover.style.top = `${Math.round(top)}px`;
  };

  const close = (): void => {
    if (closed) return;
    if (document.activeElement === hexInput) {
      commitHex();
    } else {
      flushPendingInput();
    }
    closed = true;
    window.removeEventListener('pointerdown', onOutsidePointer, true);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('resize', position);
    window.removeEventListener('scroll', onScroll, true);
    popover.remove();
    if (activePicker?.close === close) activePicker = null;
    options.onClose?.();
  };

  const onOutsidePointer = (event: PointerEvent): void => {
    const target = event.target as Node | null;
    if (target && (popover.contains(target) || options.anchor.contains(target))) return;
    close();
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };
  const onScroll = (event: Event): void => {
    if (event.target instanceof Node && popover.contains(event.target)) return;
    position();
  };

  window.addEventListener('pointerdown', onOutsidePointer, true);
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('resize', position);
  window.addEventListener('scroll', onScroll, true);

  syncControls();
  position();
  activePicker = { close };

  return {
    element: popover,
    close,
    setHex: hex => {
      const normalized = normalizeHex(hex);
      if (normalized) setColorFromHex(normalized, false);
    },
    setOpacity: value => setOpacity(value, false),
  };
}

function createEyeDropperIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.classList.add('color-picker-eyedropper-icon');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    'M14.7 4.3a2.4 2.4 0 0 1 3.4 0l1.6 1.6a2.4 2.4 0 0 1 0 3.4l-1.5 1.5 1 1-1.4 1.4-1-1-7.5 7.5H5v-4.3l7.5-7.5-1-1 1.4-1.4 1 1 1.5-1.5Z',
  );
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  path.setAttribute('stroke-width', '1.8');
  svg.appendChild(path);

  return svg;
}

function normalizeOklch(value: Oklch): Oklch {
  const l = clamp01(value.l);
  const h = ((value.h % 360) + 360) % 360;
  return {
    l,
    c: Math.min(Math.max(0, value.c), OKLCH_PICKER_MAX_CHROMA),
    h,
  };
}

function outputOklch(value: Oklch): Oklch {
  return {
    ...value,
    c: Math.min(value.c, maxOklchChromaInSrgb(value.l, value.h, OKLCH_PICKER_MAX_CHROMA)),
  };
}

function createOklchControl(
  label: string,
  initial: number,
  min: number,
  max: number,
  step: number,
  suffix: string | undefined,
  decimals: number,
  onChange: (value: number) => void,
  options: Pick<ColorPickerOptions, 'onBegin' | 'onCommit'>,
  subtype?: ValueSubtype,
  unit?: ValueUnit,
  showProgress?: boolean,
): { wrap: HTMLLabelElement; dragger: DragFieldElement; setValue: (value: number) => void } {
  const wrap = document.createElement('label');
  wrap.className = 'color-picker-oklch-field';
  const tag = document.createElement('span');
  tag.className = 'color-picker-oklch-label';
  tag.textContent = label;
  wrap.appendChild(tag);
  const dragger = createDragger(
    initial,
    min,
    max,
    step,
    onChange,
    {
      onBegin: () => options.onBegin?.(),
      onEnd: changed => {
        if (changed) options.onCommit?.();
      },
    },
    {
      decimals,
      suffix,
      className: 'color-picker-oklch-dragger',
      subtype,
      unit,
      showProgress,
    },
  );
  wrap.appendChild(dragger);
  return {
    wrap,
    dragger,
    setValue: value => dragger.setValue(value),
  };
}
